import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  List,
  Plus,
  Trash2,
  Bell,
  Star,
  Edit2,
  X,
  ChevronDown,
  ChevronRight,
  ArrowUpCircle,
  ArrowDownCircle,
  Eye,
  Zap,
  GitMerge,
  CheckCircle2,
  RefreshCw,
  Shield,
  Target,
} from 'lucide-react';
import Card from '../components/Card';
import LoadingSpinner from '../components/LoadingSpinner';
import WatchlistRow from '../components/WatchlistRow';
import OrderTicket from '../components/trading/OrderTicket';
import eodhd from '../services/eodhd';
import api, { Watchlist as APIWatchlist } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useWatchlistSignals, useTriggerScan, useActOnSignal, useDismissSignal } from '../hooks/useWatchlistSignals';
import type { WatchlistSignal } from '../types/watchlistSignals';

interface WatchlistAsset {
  symbol: string;
  name: string;
  price: number;
  change: number;
  previousClose?: number;
  alertsSet: number;
  currency: string;
}

// ─── Signal policy ─────────────────────────────────────────────────────────

function useSignalPolicy() {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: ['signal-policy'],
    queryFn: () =>
      api.request<{ org_id: number; signal_action_policy: string }>('/api/v1/orgs/signal-policy'),
    enabled: !user?.is_admin,
    retry: false,
    throwOnError: false,
  });
  return data?.signal_action_policy ?? 'open';
}

// ─── Signal helper components ──────────────────────────────────────────────

function SignalTypeBadge({ type }: { type: string }) {
  switch (type) {
    case 'BUY':
      return (
        <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-emerald-500/20 text-emerald-300">
          BUY
        </span>
      );
    case 'SELL':
      return (
        <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-red-500/20 text-red-300">
          SELL
        </span>
      );
    default:
      return (
        <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-slate-500/20 text-slate-300">
          WATCH
        </span>
      );
  }
}

function ConfidenceBadge({ confidence }: { confidence: number | null }) {
  if (confidence == null) return null;
  const pct = Math.round(confidence);
  const color =
    pct >= 75 ? 'text-emerald-400 bg-emerald-500/20' :
    pct >= 60 ? 'text-amber-400 bg-amber-500/20' :
    'text-red-400 bg-red-500/20';
  return (
    <span className={`px-2.5 py-0.5 rounded-md text-xs font-bold ${color}`}>
      {pct}%
    </span>
  );
}

function ConvergenceBadge() {
  return (
    <span className="flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-purple-500/20 text-purple-300 font-medium">
      <GitMerge size={10} />
      2/3 Convergence
    </span>
  );
}

function TripleConfirmedBadge() {
  return (
    <span className="flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-300 font-medium">
      <Zap size={10} />
      Triple Confirmed
    </span>
  );
}

// ─── Signal card ──────────────────────────────────────────────────────────

interface SignalCardProps {
  signal: WatchlistSignal;
  canActOnSignals: boolean;
  onActOn: (id: number) => void;
  isActing: boolean;
  onDismiss: (id: number) => void;
  isDismissing: boolean;
  onPlaceOrder?: (symbol: string, side: 'buy' | 'sell') => void;
}

function isTripleConfirmed(signal: WatchlistSignal): boolean {
  return signal.convergence && signal.status === 'acted_on';
}

const SignalCard: React.FC<SignalCardProps> = ({ signal, canActOnSignals, onActOn, isActing, onDismiss, isDismissing, onPlaceOrder }) => {
  const [expanded, setExpanded] = useState(false);
  const isBuy = signal.signal_type === 'BUY';
  const triple = isTripleConfirmed(signal);

  return (
    <div className="bg-slate-800/60 border border-slate-700/40 rounded-lg overflow-hidden">
      <div
        className="px-4 py-3 cursor-pointer hover:bg-slate-700/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            {isBuy ? (
              <ArrowUpCircle size={16} className="text-emerald-400 shrink-0" />
            ) : signal.signal_type === 'SELL' ? (
              <ArrowDownCircle size={16} className="text-red-400 shrink-0" />
            ) : (
              <Eye size={16} className="text-slate-400 shrink-0" />
            )}

            <span className="font-semibold text-white">{signal.symbol}</span>
            {signal.exchange && (
              <span className="text-xs text-slate-500">{signal.exchange}</span>
            )}

            <SignalTypeBadge type={signal.signal_type} />

            {triple ? (
              <TripleConfirmedBadge />
            ) : signal.convergence ? (
              <ConvergenceBadge />
            ) : null}

            {signal.strategy_matched && (
              <div className="flex items-center gap-1">
                <Target size={10} className="text-slate-500" />
                <span className="text-xs text-slate-400">
                  {signal.strategy_matched.replace(/_/g, ' ')}
                </span>
              </div>
            )}

            {signal.status === 'acted_on' && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-teal-500/20 text-teal-300 font-medium">
                Acted On
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <ConfidenceBadge confidence={signal.confidence} />
            <button
              onClick={(e) => { e.stopPropagation(); onDismiss(signal.id); }}
              disabled={isDismissing}
              className="p-0.5 text-slate-600 hover:text-slate-300 transition-colors"
              title="Dismiss signal"
            >
              <X size={14} />
            </button>
            {expanded ? (
              <ChevronDown size={14} className="text-slate-500" />
            ) : (
              <ChevronRight size={14} className="text-slate-500" />
            )}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 bg-slate-800/30 border-t border-slate-700/40">
          {signal.chart_image && (
            <div className="mt-3">
              <h4 className="text-xs text-slate-500 uppercase tracking-wide mb-2">Price Chart (90d)</h4>
              <img
                src={`data:image/png;base64,${signal.chart_image}`}
                alt={`${signal.symbol} 90-day chart`}
                className="w-full rounded-lg border border-slate-700/50"
              />
            </div>
          )}
          {signal.thesis && (
            <div>
              <h4 className="text-xs text-slate-500 uppercase tracking-wide mb-1 mt-3">Thesis</h4>
              <p className="text-sm text-slate-300">{signal.thesis}</p>
            </div>
          )}
          {signal.technical_rationale && (
            <div>
              <h4 className="text-xs text-slate-500 uppercase tracking-wide mb-1">
                Technical Rationale
              </h4>
              <p className="text-sm text-slate-300">{signal.technical_rationale}</p>
            </div>
          )}
          {signal.contra_summary && (
            <div>
              <h4 className="text-xs text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1">
                <Shield size={12} /> Contra Assessment
              </h4>
              <p className="text-sm text-slate-300">{signal.contra_summary}</p>
            </div>
          )}

          <div className="flex items-center gap-4 text-xs text-slate-500 pt-1">
            {signal.created_at && (
              <span>Created: {new Date(signal.created_at).toLocaleString()}</span>
            )}
            {signal.acted_on_at && (
              <span>Acted: {new Date(signal.acted_on_at).toLocaleString()}</span>
            )}
          </div>

          {((onPlaceOrder && signal.signal_type !== 'WATCH') || (canActOnSignals && signal.status !== 'acted_on')) && (
            <div className="pt-2 border-t border-slate-700/30 flex items-center gap-2 flex-wrap">
              {onPlaceOrder && signal.signal_type !== 'WATCH' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onPlaceOrder(signal.symbol, signal.signal_type === 'BUY' ? 'buy' : 'sell');
                  }}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    signal.signal_type === 'BUY'
                      ? 'bg-green-600 hover:bg-green-500 text-white'
                      : 'bg-red-600 hover:bg-red-500 text-white'
                  }`}
                >
                  {signal.signal_type === 'BUY' ? 'Buy' : 'Sell'} {signal.symbol.split('.')[0]}
                </button>
              )}
              {canActOnSignals && signal.status !== 'acted_on' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onActOn(signal.id);
                  }}
                  disabled={isActing}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-slate-700 text-slate-300 hover:bg-slate-600 disabled:opacity-50"
                >
                  Mark as Acted On
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Signal grouping helpers ──────────────────────────────────────────────

interface SignalSymbolGroup {
  symbol: string;
  exchange: string;
  signals: WatchlistSignal[];
  bestConfidence: number;
  hasConvergence: boolean;
  types: Set<string>;
}

interface SignalExchangeGroup {
  exchange: string;
  symbols: SignalSymbolGroup[];
}

function extractSignalExchange(signal: WatchlistSignal): string {
  if (signal.exchange) return signal.exchange.toUpperCase();
  const parts = signal.symbol?.split('.');
  return parts && parts.length > 1 ? parts[parts.length - 1].toUpperCase() : 'OTHER';
}

function groupSignalsByExchangeThenSymbol(signals: WatchlistSignal[]): SignalExchangeGroup[] {
  const byExchange = new Map<string, Map<string, WatchlistSignal[]>>();

  for (const signal of signals) {
    const exchange = extractSignalExchange(signal);
    const symbol = signal.symbol ?? '';
    if (!byExchange.has(exchange)) byExchange.set(exchange, new Map());
    const bySymbol = byExchange.get(exchange)!;
    if (!bySymbol.has(symbol)) bySymbol.set(symbol, []);
    bySymbol.get(symbol)!.push(signal);
  }

  return Array.from(byExchange.entries())
    .map(([exchange, symbolMap]) => ({
      exchange,
      symbols: Array.from(symbolMap.entries())
        .map(([symbol, symbolSignals]) => {
          const sorted = [...symbolSignals].sort((a, b) => {
            const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
            const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
            return tb - ta; // most recent first
          });
          return {
            symbol,
            exchange,
            signals: sorted,
            bestConfidence: Math.max(...symbolSignals.map((s) => s.confidence ?? 0)),
            hasConvergence: symbolSignals.some((s) => s.convergence),
            types: new Set(symbolSignals.map((s) => s.signal_type)),
          };
        })
        .sort((a, b) => b.bestConfidence - a.bestConfidence),
    }))
    .sort((a, b) => a.exchange.localeCompare(b.exchange));
}

// ─── Symbol group accordion ───────────────────────────────────────────────

interface SymbolGroupProps {
  group: SignalSymbolGroup;
  isOpen: boolean;
  onToggle: () => void;
  canActOnSignals: boolean;
  onActOn: (id: number) => void;
  isActing: boolean;
  onDismiss: (id: number) => void;
  isDismissing: boolean;
  onPlaceOrder?: (symbol: string, side: 'buy' | 'sell') => void;
}

const SymbolGroupRow: React.FC<SymbolGroupProps> = ({
  group, isOpen, onToggle, canActOnSignals, onActOn, isActing, onDismiss, isDismissing, onPlaceOrder,
}) => {
  const hasBuy = group.types.has('BUY');
  const hasSell = group.types.has('SELL');
  const hasWatch = group.types.has('WATCH');

  return (
    <div className="bg-slate-800/40 border border-slate-700/40 rounded-lg overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-700/30 transition-colors text-left"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          {hasBuy && !hasSell && <ArrowUpCircle size={15} className="text-emerald-400 shrink-0" />}
          {hasSell && !hasBuy && <ArrowDownCircle size={15} className="text-red-400 shrink-0" />}
          {((hasBuy && hasSell) || hasWatch) && <Eye size={15} className="text-slate-400 shrink-0" />}

          <span className="font-semibold text-white text-sm">{group.symbol}</span>

          <div className="flex items-center gap-1">
            {hasBuy && <SignalTypeBadge type="BUY" />}
            {hasSell && <SignalTypeBadge type="SELL" />}
            {hasWatch && <SignalTypeBadge type="WATCH" />}
          </div>

          {group.hasConvergence && (
            <span className="flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-purple-500/20 text-purple-300 font-medium">
              <GitMerge size={10} />
              Convergence
            </span>
          )}

          <span className="text-xs text-slate-500">{group.signals.length} signal{group.signals.length !== 1 ? 's' : ''}</span>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {group.bestConfidence > 0 && <ConfidenceBadge confidence={group.bestConfidence} />}
          {isOpen ? <ChevronDown size={14} className="text-slate-500" /> : <ChevronRight size={14} className="text-slate-500" />}
        </div>
      </button>

      {isOpen && (
        <div className="px-3 pb-3 space-y-2 bg-slate-800/20 border-t border-slate-700/30">
          {group.signals.map((signal) => (
            <SignalCard
              key={signal.id}
              signal={signal}
              canActOnSignals={canActOnSignals}
              onActOn={onActOn}
              isActing={isActing}
              onDismiss={onDismiss}
              isDismissing={isDismissing}
              onPlaceOrder={onPlaceOrder}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Section accordion wrapper ────────────────────────────────────────────

interface SectionProps {
  title: string;
  subtitle?: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  badge?: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, subtitle, open, onToggle, children, badge }) => (
  <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
    <button
      className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-700/30 transition-colors"
      onClick={onToggle}
    >
      <div className="flex items-center gap-3">
        <span className="font-semibold text-white">{title}</span>
        {badge}
        {subtitle && <span className="text-xs text-slate-500">{subtitle}</span>}
      </div>
      {open ? (
        <ChevronDown size={16} className="text-slate-400" />
      ) : (
        <ChevronRight size={16} className="text-slate-400" />
      )}
    </button>
    {open && <div className="px-5 pb-5">{children}</div>}
  </div>
);

// ─── Signal type filter ────────────────────────────────────────────────────

type SignalFilter = '' | 'BUY' | 'SELL' | 'WATCH';

// ─── Main page ────────────────────────────────────────────────────────────

const Watchlist: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [watchlists, setWatchlists] = useState<APIWatchlist[]>([]);
  const [activeWatchlist, setActiveWatchlist] = useState<number | null>(null);
  const [assets, setAssets] = useState<WatchlistAsset[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSymbol, setNewSymbol] = useState('');
  const [editingName, setEditingName] = useState<number | null>(null);
  const [newWatchlistName, setNewWatchlistName] = useState('');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Accordion open state — assets open by default, signals collapsed
  const [assetsOpen, setAssetsOpen] = useState(true);
  const [signalsOpen, setSignalsOpen] = useState(false);

  // Signal grouping expand state: exchange → symbol
  const [expandedSignalExchange, setExpandedSignalExchange] = useState<string | null>(null);
  const [expandedSignalSymbol, setExpandedSignalSymbol] = useState<string | null>(null);

  // Order ticket state
  const [orderTicket, setOrderTicket] = useState<{ symbol: string; price: number; defaultSide?: 'buy' | 'sell' } | null>(null);

  const handleBuy = (symbol: string, price: number) => {
    setOrderTicket({ symbol, price });
  };

  const handleSignalOrder = (symbol: string, side: 'buy' | 'sell') => {
    const asset = assets.find((a) => a.symbol === symbol);
    setOrderTicket({ symbol, price: asset?.price ?? 0, defaultSide: side });
  };

  // Signals state
  const [signalFilter, setSignalFilter] = useState<SignalFilter>('');
  const [convergenceOnly, setConvergenceOnly] = useState(false);
  const signalPolicy = useSignalPolicy();
  const canActOnSignals = signalPolicy !== 'notify_only';

  const { data: signalData, isLoading: signalsLoading, error: signalsError } = useWatchlistSignals({
    limit: 100,
    signal_type: signalFilter || undefined,
    convergence_only: convergenceOnly || undefined,
  });
  const triggerScan = useTriggerScan();
  const actOnSignal = useActOnSignal();
  const dismissSignal = useDismissSignal();

  const signals = signalData?.items ?? [];

  const lastSignalAt = signals[0]?.created_at ?? null;
  const lastScanTime = lastSignalAt ? new Date(lastSignalAt).toLocaleString() : null;
  const todayCompact = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const scanIdSet = new Set(signals.map((s) => s.scan_id).filter(Boolean));
  const scansToday = [...scanIdSet].filter((id) => id?.startsWith(todayCompact)).length;

  useEffect(() => {
    loadWatchlists();
  }, []);

  useEffect(() => {
    if (watchlists.length > 0 && activeWatchlist !== null) {
      loadWatchlistData();
    }
  }, [activeWatchlist, watchlists]);

  const loadWatchlists = async () => {
    try {
      const lists = await api.getWatchlists();
      if (!lists || lists.length === 0) {
        const newList = await api.createWatchlist('Main Watchlist');
        setWatchlists([newList]);
        setActiveWatchlist(newList.id);
      } else {
        setWatchlists(lists);
        setActiveWatchlist(lists[0].id);
      }
    } catch (error) {
      console.error('Error loading watchlists:', error);
      setLoading(false);
    }
  };

  const loadWatchlistData = async () => {
    try {
      setLoading(true);
      const currentList = watchlists.find((w) => w.id === activeWatchlist);
      if (!currentList || currentList.items.length === 0) {
        setAssets([]);
        return;
      }

      const symbols = currentList.items.map((i) => i.symbol);
      const quotes = await eodhd.getBulkQuotes(symbols);

      const quoteMap = new Map<string, (typeof quotes)[0]>();
      for (const q of quotes) {
        if (q.symbol) quoteMap.set(q.symbol, q);
        if (q.code && !quoteMap.has(q.code)) quoteMap.set(q.code, q);
      }

      const results: WatchlistAsset[] = currentList.items.map((item) => {
        const quote = quoteMap.get(item.symbol) ?? quoteMap.get(item.symbol.split('.')[0]);
        const isJSE = item.symbol.endsWith('.JSE');
        return {
          symbol: item.symbol,
          name: item.name || item.symbol.split('.')[0],
          price: quote?.close ?? 0,
          change: quote ? (quote.change_percent ?? quote.change_p ?? 0) : 0,
          previousClose: quote?.previousClose ?? quote?.previous_close,
          alertsSet: 0,
          currency: isJSE ? 'R' : '$',
        };
      });

      setAssets(results);
    } catch (error) {
      console.error('Error loading watchlist data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addSymbol = async () => {
    if (!newSymbol.trim() || activeWatchlist === null) return;
    try {
      const symbol = newSymbol.toUpperCase();
      const name = symbol.split('.')[0];
      const exchange = symbol.includes('.') ? symbol.split('.')[1] : 'Unknown';
      await api.addWatchlistItem(activeWatchlist, symbol, name, exchange);
      setNewSymbol('');
      setShowAddModal(false);
      await loadWatchlists();
    } catch (error) {
      console.error('Error adding symbol:', error);
      alert('Failed to add symbol. Please try again.');
    }
  };

  const removeSymbol = async (symbol: string) => {
    if (activeWatchlist === null) return;
    try {
      const currentList = watchlists.find((w) => w.id === activeWatchlist);
      if (!currentList) return;
      const item = currentList.items.find((i) => i.symbol === symbol);
      if (!item) return;
      await api.removeWatchlistItem(activeWatchlist, item.id);
      await loadWatchlists();
    } catch (error) {
      console.error('Error removing symbol:', error);
      alert('Failed to remove symbol. Please try again.');
    }
  };

  const createWatchlist = async () => {
    if (!newWatchlistName.trim()) return;
    try {
      const newList = await api.createWatchlist(newWatchlistName);
      await loadWatchlists();
      setActiveWatchlist(newList.id);
      setNewWatchlistName('');
    } catch (error) {
      console.error('Error creating watchlist:', error);
      alert('Failed to create watchlist. Please try again.');
    }
  };

  const deleteWatchlist = async (id: number) => {
    if (watchlists.length <= 1) return;
    try {
      await api.deleteWatchlist(id);
      await loadWatchlists();
      if (activeWatchlist === id) {
        const remaining = watchlists.filter((w) => w.id !== id);
        if (remaining.length > 0) setActiveWatchlist(remaining[0].id);
      }
    } catch (error) {
      console.error('Error deleting watchlist:', error);
      alert('Failed to delete watchlist. Please try again.');
    }
  };

  const renameWatchlist = async (id: number, newName: string) => {
    if (!newName.trim()) return;
    try {
      await api.updateWatchlist(id, newName);
      await loadWatchlists();
      setEditingName(null);
    } catch (error) {
      console.error('Error renaming watchlist:', error);
      alert('Failed to rename watchlist. Please try again.');
    }
  };

  const handleDragStart = (index: number) => setDraggedIndex(index);

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === targetIndex) return;
    const newAssetsState = [...assets];
    const [draggedAsset] = newAssetsState.splice(draggedIndex, 1);
    newAssetsState.splice(targetIndex, 0, draggedAsset);
    setAssets(newAssetsState);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const currentWatchlist = watchlists.find((w) => w.id === activeWatchlist);

  return (
    <div className="p-3 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <List className="text-teal-400" />
            Watchlists
          </h1>
          <p className="text-sm text-slate-400 mt-1">Track your favorite assets and get LLM-powered signals</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold rounded-lg transition-colors"
        >
          <Plus size={16} />
          Add Symbol
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Watchlist Sidebar */}
        <div className="space-y-4">
          <Card title="My Watchlists">
            <div className="space-y-2">
              {watchlists.map((list) => (
                <div
                  key={list.id}
                  className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors group ${
                    activeWatchlist === list.id ? 'bg-teal-500/20 text-teal-400' : 'hover:bg-slate-700/50'
                  }`}
                  onClick={() => setActiveWatchlist(list.id)}
                >
                  {editingName === list.id ? (
                    <input
                      type="text"
                      defaultValue={list.name}
                      className="bg-slate-700 px-2 py-1 rounded text-sm w-full mr-2"
                      onBlur={(e) => renameWatchlist(list.id, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') renameWatchlist(list.id, e.currentTarget.value);
                      }}
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <Star size={14} className={activeWatchlist === list.id ? 'text-teal-400' : 'text-slate-500'} />
                      <span className="text-sm">{list.name}</span>
                      <span className="text-xs text-slate-500">({list.items.length})</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingName(list.id);
                      }}
                      className="p-1 hover:bg-slate-600 rounded"
                    >
                      <Edit2 size={12} />
                    </button>
                    {watchlists.length > 1 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteWatchlist(list.id);
                        }}
                        className="p-1 hover:bg-red-500/20 hover:text-red-400 rounded"
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-slate-700/50">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="New watchlist name"
                  value={newWatchlistName}
                  onChange={(e) => setNewWatchlistName(e.target.value)}
                  className="flex-1 bg-slate-700 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                  onKeyDown={(e) => e.key === 'Enter' && createWatchlist()}
                />
                <button
                  onClick={createWatchlist}
                  className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>
          </Card>
        </div>

        {/* Main Content — two collapsible sections */}
        <div className="lg:col-span-3 space-y-4">

          {/* ── Section 1: Assets ── */}
          <Section
            title={currentWatchlist?.name || 'Watchlist'}
            subtitle={assets.length > 0 ? `${assets.length} asset${assets.length !== 1 ? 's' : ''}` : undefined}
            open={assetsOpen}
            onToggle={() => setAssetsOpen(!assetsOpen)}
          >
            {loading ? (
              <LoadingSpinner />
            ) : assets.length === 0 ? (
              <div className="text-center py-12">
                <Star size={48} className="mx-auto text-slate-600 mb-4" />
                <p className="text-slate-400">No assets in this watchlist</p>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="mt-4 text-teal-400 hover:text-teal-300 text-sm"
                >
                  + Add your first symbol
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 uppercase border-b border-slate-700/50">
                      <th className="pb-3 w-8"></th>
                      <th className="pb-3 font-medium">Asset</th>
                      <th className="pb-3 font-medium text-right">Price</th>
                      <th className="pb-3 font-medium text-right">Change %</th>
                      <th className="pb-3 font-medium text-center">Alerts</th>
                      <th className="pb-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assets.map((asset, i) => (
                      <WatchlistRow
                        key={i}
                        symbol={asset.symbol}
                        name={asset.name}
                        staticPrice={asset.price}
                        staticChange={asset.change}
                        previousClose={asset.previousClose}
                        alertsSet={asset.alertsSet}
                        currency={asset.currency}
                        onDelete={() => removeSymbol(asset.symbol)}
                        onSetAlert={() => { /* TODO */ }}
                        onBuy={handleBuy}
                        draggable={true}
                        onDragStart={() => handleDragStart(i)}
                        onDragOver={() => handleDragOver(new Event('dragover') as unknown as React.DragEvent, i)}
                        onDrop={() => handleDrop(new Event('drop') as unknown as React.DragEvent, i)}
                        isDragging={draggedIndex === i}
                        isDragOver={dragOverIndex === i && draggedIndex !== i}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* ── Section 2: Signals ── */}
          <Section
            title="Signals"
            open={signalsOpen}
            onToggle={() => setSignalsOpen(!signalsOpen)}
            badge={
              signalData && signalData.total > 0 ? (
                <span className="px-2 py-0.5 text-xs rounded-full bg-teal-500/20 text-teal-300 font-medium">
                  {signalData.total}
                </span>
              ) : undefined
            }
            subtitle="LLM-powered signals with banker lead convergence"
          >
            {/* Scan status bar */}
            <div className="flex flex-wrap items-center justify-between gap-4 bg-slate-800/50 border border-slate-700/40 rounded-xl px-5 py-3 mb-4">
              <div className="flex items-center gap-6">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Last Scan</p>
                  <p className="text-sm font-medium text-slate-300 mt-0.5">
                    {lastScanTime ?? 'No scans yet'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Scans Today</p>
                  <p className="text-sm font-medium text-slate-300 mt-0.5">{scansToday}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Total Signals</p>
                  <p className="text-sm font-medium text-slate-300 mt-0.5">{signalData?.total ?? 0}</p>
                </div>
              </div>

              <button
                onClick={() => triggerScan.mutate()}
                disabled={triggerScan.isPending}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors
                  bg-teal-600 hover:bg-teal-500 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw size={15} className={triggerScan.isPending ? 'animate-spin' : ''} />
                {triggerScan.isPending ? 'Scanning...' : 'Scan Now'}
              </button>
            </div>

            {/* Scan result feedback */}
            {triggerScan.isSuccess && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-4 py-3 text-sm text-emerald-300 mb-4">
                <CheckCircle2 size={14} className="inline mr-2" />
                Scan complete — {triggerScan.data.signals_generated} signal
                {triggerScan.data.signals_generated !== 1 ? 's' : ''} generated
                {triggerScan.data.convergence_count > 0
                  ? `, ${triggerScan.data.convergence_count} convergence${
                      triggerScan.data.convergence_count !== 1 ? 's' : ''
                    }`
                  : ''}
                .
              </div>
            )}
            {triggerScan.isError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-300 mb-4">
                Scan failed:{' '}
                {triggerScan.error instanceof Error ? triggerScan.error.message : 'Unknown error'}
              </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="flex items-center gap-1 bg-slate-800/50 border border-slate-700/50 rounded-lg p-1">
                {(['', 'BUY', 'SELL', 'WATCH'] as SignalFilter[]).map((f) => (
                  <button
                    key={f}
                    onClick={() => setSignalFilter(f)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      signalFilter === f
                        ? 'bg-teal-600 text-white'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'
                    }`}
                  >
                    {f === '' ? 'All' : f}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setConvergenceOnly(!convergenceOnly)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                  convergenceOnly
                    ? 'border-purple-500/60 bg-purple-500/15 text-purple-300'
                    : 'border-slate-700/50 text-slate-400 hover:text-slate-200 hover:bg-slate-700/30'
                }`}
              >
                <GitMerge size={12} />
                Convergence Only
              </button>
            </div>

            {/* Signal list — grouped by exchange → symbol, most recent first */}
            {signalsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin" />
              </div>
            ) : signalsError ? (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-300">
                Failed to load signals. Please try again.
              </div>
            ) : signals.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center space-y-3">
                <Eye size={36} className="text-slate-600" />
                <p className="text-slate-400 font-medium">No signals yet</p>
                <p className="text-slate-500 text-sm">
                  Click "Scan Now" to generate signals for your watchlist assets.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {groupSignalsByExchangeThenSymbol(signals).map((exGroup) => (
                  <div key={exGroup.exchange} className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
                    {/* Exchange header */}
                    <button
                      className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-700/30 transition-colors"
                      onClick={() => {
                        const next = expandedSignalExchange === exGroup.exchange ? null : exGroup.exchange;
                        setExpandedSignalExchange(next);
                        setExpandedSignalSymbol(null); // reset symbol selection on every exchange toggle
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-white text-sm">{exGroup.exchange}</span>
                        <span className="text-xs text-slate-500">
                          {exGroup.symbols.length} symbol{exGroup.symbols.length !== 1 ? 's' : ''} · {exGroup.symbols.reduce((n, s) => n + s.signals.length, 0)} signals
                        </span>
                      </div>
                      {expandedSignalExchange === exGroup.exchange ? (
                        <ChevronDown size={14} className="text-slate-400" />
                      ) : (
                        <ChevronRight size={14} className="text-slate-400" />
                      )}
                    </button>

                    {/* Symbol groups within exchange */}
                    {expandedSignalExchange === exGroup.exchange && (
                      <div className="px-4 pt-3 pb-4 space-y-2">
                        {exGroup.symbols.map((symGroup) => {
                          const key = `${exGroup.exchange}::${symGroup.symbol}`;
                          return (
                            <SymbolGroupRow
                              key={key}
                              group={symGroup}
                              isOpen={expandedSignalSymbol === key}
                              onToggle={() =>
                                setExpandedSignalSymbol(expandedSignalSymbol === key ? null : key)
                              }
                              canActOnSignals={canActOnSignals}
                              onActOn={(id) => actOnSignal.mutate(id)}
                              isActing={actOnSignal.isPending}
                              onDismiss={(id) => dismissSignal.mutate(id)}
                              isDismissing={dismissSignal.isPending}
                              onPlaceOrder={handleSignalOrder}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      </div>

      {/* Order Ticket Modal */}
      <OrderTicket
        symbol={orderTicket?.symbol ?? ''}
        currentPrice={orderTicket?.price ?? 0}
        defaultSide={orderTicket?.defaultSide}
        isOpen={!!orderTicket}
        onClose={() => setOrderTicket(null)}
      />

      {/* Add Symbol Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-sm mx-4 sm:mx-0 border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add Symbol</h3>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-slate-400 mb-4">
              Enter a symbol to add to "{currentWatchlist?.name}"
            </p>
            <input
              type="text"
              placeholder="e.g., AAPL.US, NPN.JSE, BTC-USD.CC"
              value={newSymbol}
              onChange={(e) => setNewSymbol(e.target.value)}
              className="w-full bg-slate-700 px-4 py-3 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-teal-500"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && addSymbol()}
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={addSymbol}
                className="flex-1 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold rounded-lg transition-colors"
              >
                Add Symbol
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Watchlist;
